import {
  LLMEventEmitter,
  type BudgetManagerConfig,
  type ChatMessage,
  type OperationType,
  type TokenUsage,
} from '@ownyou/llm-client';
import {
  executeLlmTextRoute,
  LlmRouteExecutionError,
  type LlmRouteExecutionErrorCode,
  type LlmRouteModelCatalogEntry,
  type LlmRouteProviderFactory,
  type LlmRouteSelectionSource,
  type LlmRouteTelemetryEvent,
  type LlmRouteTextResult,
} from './executor';
import { getModelRouteDefinition, type ModelRouteId } from './routes';

export type RouteLocalOutcomeStatus = 'success' | 'parse_failed' | 'validation_failed' | 'not_run';
export type RouteLocalOutcomeResult =
  | 'success'
  | 'fallback'
  | 'surfaced_failure'
  | 'skipped'
  | 'no_op';

export interface RouteAdapterResolvedRoute {
  routeId: ModelRouteId;
  provider: NonNullable<LlmRouteTelemetryEvent['provider']>;
  model: string;
  selectionSource: LlmRouteSelectionSource;
  reason: string;
}

export interface RouteAdapterUnavailableRoute {
  routeId: ModelRouteId;
  reason: string;
  savedSelection?: {
    provider: string;
    model: string;
  };
}

export interface RouteAdapterPlan {
  routes: Partial<Record<ModelRouteId, RouteAdapterResolvedRoute>>;
  unavailable: RouteAdapterUnavailableRoute[];
}

export interface RouteLlmClientAdapterBaseOptions<TPlan extends RouteAdapterPlan> {
  routePlan?: TPlan | null;
  authToken?: string | null;
  userId: string;
  budgetConfig?: BudgetManagerConfig;
  modelCatalog?: readonly LlmRouteModelCatalogEntry[];
  providerFactory?: LlmRouteProviderFactory;
  emitTelemetry?: (event: LlmRouteTelemetryEvent) => void;
  agentLabel: string;
  routeFamilyLabel: string;
}

export interface RouteTextExecutionInput<TRouteOperation extends string> {
  routeId: ModelRouteId;
  routeOperation: TRouteOperation;
  executorOperation: OperationType;
  userId: string;
  messages: ChatMessage[];
  temperature?: number;
  maxTokens?: number;
}

export interface UnavailableRouteInput<TRouteOperation extends string> {
  routeId: ModelRouteId;
  routeOperation: TRouteOperation;
  executorOperation: OperationType;
  code: LlmRouteExecutionErrorCode;
}

export function emptyUsage(): TokenUsage {
  return {
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    costUsd: 0,
  };
}

export function routeMetadata(routeId: ModelRouteId) {
  const route = getModelRouteDefinition(routeId);
  return {
    routePolicyVersion: route?.routePolicyVersion ?? null,
    promptVersion: route?.promptVersion ?? null,
    outputSchemaVersion: route?.outputSchemaVersion ?? null,
    privacyClass: route?.modelFitPolicy.privacyPosture ?? null,
  };
}

export function unavailableTelemetry(
  routeId: ModelRouteId,
  operation: OperationType,
  errorCode: LlmRouteExecutionErrorCode
): LlmRouteTelemetryEvent {
  return {
    type: 'llm_route_execution',
    routeId,
    provider: null,
    model: null,
    selectionSource: null,
    selectionReason: null,
    ...routeMetadata(routeId),
    validation: {
      status: 'not_run',
      policyKind: getModelRouteDefinition(routeId)?.validationPolicy.kind,
    },
    result: 'error',
    errorCode,
    latencyMs: 0,
    costKnown: false,
    costBucket: 'not_applicable',
    budgetStatus: 'not_checked',
    operation,
  };
}

export abstract class RouteLlmClientAdapterBase<
  TPlan extends RouteAdapterPlan,
  TRouteOperation extends string,
> {
  public readonly events = new LLMEventEmitter();

  protected constructor(protected readonly routeOptions: RouteLlmClientAdapterBaseOptions<TPlan>) {}

  protected async executeRouteText(
    input: RouteTextExecutionInput<TRouteOperation>
  ): Promise<LlmRouteTextResult & { usage: TokenUsage }> {
    const resolved = this.resolveRoute(
      input.routeId,
      input.routeOperation,
      input.executorOperation
    );
    const started = Date.now();
    const result = await executeLlmTextRoute({
      routeId: input.routeId,
      selection: {
        provider: resolved.provider,
        model: resolved.model,
      },
      selectionSource: resolved.selectionSource,
      selectionReason: resolved.reason,
      messages: input.messages,
      temperature: input.temperature,
      maxTokens: input.maxTokens,
      operation: input.executorOperation,
      userId: input.userId,
      authToken: this.routeOptions.authToken,
      budgetConfig: this.routeOptions.budgetConfig,
      modelCatalog: this.routeOptions.modelCatalog,
      providerFactory: this.routeOptions.providerFactory,
      emitTelemetry: this.routeOptions.emitTelemetry,
    });

    const usage = result.usage ?? emptyUsage();
    this.events.emit('usage', {
      model: result.model,
      operation: input.executorOperation,
      inputTokens: usage.inputTokens,
      outputTokens: usage.outputTokens,
      costUsd: usage.costUsd,
      latencyMs: Date.now() - started,
      timestamp: Date.now(),
      userId: input.userId,
      agent: this.routeOptions.agentLabel,
    });

    return {
      ...result,
      usage,
    };
  }

  private resolveRoute(
    routeId: ModelRouteId,
    routeOperation: TRouteOperation,
    executorOperation: OperationType
  ): RouteAdapterResolvedRoute {
    const plan = this.routeOptions.routePlan;
    if (!plan) {
      this.failUnavailable(
        routeId,
        routeOperation,
        executorOperation,
        'missing_route_plan',
        `${this.routeOptions.routeFamilyLabel} route plan is missing`
      );
    }

    const resolved = plan.routes[routeId];
    if (resolved) return resolved;

    const unavailable = plan.unavailable.find(route => route.routeId === routeId);
    if (unavailable) {
      this.failUnavailable(
        routeId,
        routeOperation,
        executorOperation,
        'unavailable_selection',
        unavailable.reason,
        {
          savedSelection: unavailable.savedSelection,
        }
      );
    }

    this.failUnavailable(
      routeId,
      routeOperation,
      executorOperation,
      'missing_route_selection',
      `${this.routeOptions.routeFamilyLabel} route selection is missing from the plan`
    );
  }

  protected recordUnavailableOutcome(_input: UnavailableRouteInput<TRouteOperation>): void {
    // Adapters with route-local fallback telemetry can override this hook.
  }

  private failUnavailable(
    routeId: ModelRouteId,
    routeOperation: TRouteOperation,
    executorOperation: OperationType,
    code: LlmRouteExecutionErrorCode,
    message: string,
    details?: Record<string, unknown>
  ): never {
    this.routeOptions.emitTelemetry?.(unavailableTelemetry(routeId, executorOperation, code));
    this.recordUnavailableOutcome({
      routeId,
      routeOperation,
      executorOperation,
      code,
    });
    throw new LlmRouteExecutionError(code, message, {
      routeId,
      details,
    });
  }
}
